import { ApolloClient, ApolloQueryResult, gql } from 'apollo-boost';
import { FetchResult } from 'apollo-link';
import {
  AnswerInput,
  ProgramAnswer,
  ProgramQuestionsResult,
  ProgramQuestionsSection,
  ProgramSummary,
} from './types';

const enumResponseFragment = `
  dbId
  order
  responseType
  response
  responseValue
  responseHelp
  questionId
  nextQuestionId
  treeGroup
`;

const validationFragment = `
  dbId
  validation
  validationType
  text
`;

const programAnswerFragment = `
  id
  dbId
  questionId
  programId
  text
  date1
  date2
  answerId
  answerTime
  answerUserId
  questionText
  autoAnswered
  resourceId
  enabled
  createdAt
  updatedAt
  enumResponse { ${enumResponseFragment} }
`;

const programQuestionFragment = `
  questionId
  text
  name
  section
  sectionStart
  questionType
  required
  helpContent
  reportingType
  nextQuestionId
  isAnswered
  enumResponses { ${enumResponseFragment} }
  validations { ${validationFragment} }
  answer { ${programAnswerFragment} }
`;

const programSectionFragment = `
  section
  total
  answered
  firstUnansweredQuestionId
  questions { ${programQuestionFragment} }
`;

const answerSavePayloadFragment = `
  success
  id
  nextUnansweredQuestionId
  completed
  answer { ${programAnswerFragment} }
  section { ${programSectionFragment} }
`;

const programRowFragment = `
  dbId
  name
  isCompleted
`;

/**
 * Program-questions query — the wizard's single source of truth.
 * Each question carries its enumResponses + validations, and each
 * answer carries the joined enumResponse row, so a wizard render
 * needs no follow-up fetches.
 */
export const programQuestionsQuery = gql`
  query programQuestions($programId: Int!) {
    programQuestions(programId: $programId) {
      programId
      totalQuestions
      answeredCount
      completed
      nextUnansweredQuestionId
      sections { ${programSectionFragment} }
    }
  }
`;

export const programSummaryQuery = gql`
  query programSummary($id: ID) {
    allPrograms(filters: { id: $id }) {
      edges {
        node {
          dbId
          name
          isCompleted
        }
      }
    }
  }
`;

export const newProgramAnswerMutation = gql`
  mutation newProgramAnswer(
    $programId: Int!
    $questionId: Int!
    $text: String
    $date1: Date
    $date2: Date
    $answerId: Int
    $questionText: String
    $autoAnswered: Boolean
    $resourceId: Int
  ) {
    newProgramAnswer(
      programId: $programId
      questionId: $questionId
      text: $text
      date1: $date1
      date2: $date2
      answerId: $answerId
      questionText: $questionText
      autoAnswered: $autoAnswered
      resourceId: $resourceId
    ) { ${answerSavePayloadFragment} }
  }
`;

export const updateProgramAnswerMutation = gql`
  mutation updateProgramAnswer(
    $id: Int!
    $text: String
    $date1: Date
    $date2: Date
    $answerId: Int
    $questionText: String
    $autoAnswered: Boolean
    $resourceId: Int
  ) {
    updateProgramAnswer(
      id: $id
      text: $text
      date1: $date1
      date2: $date2
      answerId: $answerId
      questionText: $questionText
      autoAnswered: $autoAnswered
      resourceId: $resourceId
    ) { ${answerSavePayloadFragment} }
  }
`;

export const deleteProgramAnswerMutation = gql`
  mutation deleteProgramAnswer($id: Int!) {
    deleteProgramAnswer(id: $id) {
      success
      id
    }
  }
`;

export const updateProgramMutation = gql`
  mutation updateProgramWizard(
    $id: Int!
    $name: String
    $isCompleted: Boolean
  ) {
    updateProgram(id: $id, name: $name, isCompleted: $isCompleted) {
      success
      id
      program { ${programRowFragment} }
    }
  }
`;

export const copyProgramAnswersToReportMutation = gql`
  mutation copyProgramAnswersToReport($reportId: Int!) {
    copyProgramAnswersToReport(reportId: $reportId) {
      success
      id
      copiedCount
      createdCount
      updatedCount
    }
  }
`;

// ---- Client wrappers -------------------------------------------------

export interface AnswerSavePayload {
  success: boolean;
  id: number | null;
  nextUnansweredQuestionId: number | null;
  completed: boolean | null;
  answer: ProgramAnswer | null;
  section: ProgramQuestionsSection | null;
}

export interface UpdateProgramPayload {
  success: boolean;
  id: number;
  program: ProgramSummary | null;
}

export interface CopyProgramAnswersPayload {
  success: boolean;
  id: number;
  copiedCount: number;
  createdCount: number;
  updatedCount: number;
}

export interface DeleteProgramAnswerPayload {
  success: boolean;
  id: number;
}

export const fetchProgramQuestions = async (
  client: ApolloClient<object>,
  programId: number,
): Promise<ApolloQueryResult<ProgramQuestionsResult>> => {
  const res = await client.query<{
    programQuestions: ProgramQuestionsResult;
  }>({
    query: programQuestionsQuery,
    variables: { programId },
    fetchPolicy: 'network-only',
  });
  const data = res.data?.programQuestions;
  if (!data) throw new Error('No program questions returned.');
  return { ...res, data };
};

export const fetchProgramSummary = async (
  client: ApolloClient<object>,
  programId: number,
): Promise<ApolloQueryResult<ProgramSummary>> => {
  const res = await client.query<{
    allPrograms: { edges: Array<{ node: ProgramSummary }> };
  }>({
    query: programSummaryQuery,
    variables: { id: String(programId) },
    fetchPolicy: 'network-only',
  });
  const data = res.data?.allPrograms?.edges?.[0]?.node;
  if (!data) throw new Error('Program not found.');
  return { ...res, data };
};

export const saveProgramAnswer = async (
  client: ApolloClient<object>,
  programId: number,
  questionId: number,
  input: AnswerInput,
): Promise<FetchResult<AnswerSavePayload | null>> => {
  const res = await client.mutate<{ newProgramAnswer: AnswerSavePayload }>({
    mutation: newProgramAnswerMutation,
    variables: { programId, questionId, ...input },
    fetchPolicy: 'no-cache',
  });
  return { ...res, data: res.data?.newProgramAnswer ?? null };
};

export const updateProgramAnswer = async (
  client: ApolloClient<object>,
  id: number,
  input: AnswerInput,
): Promise<FetchResult<AnswerSavePayload | null>> => {
  const res = await client.mutate<{ updateProgramAnswer: AnswerSavePayload }>({
    mutation: updateProgramAnswerMutation,
    variables: { id, ...input },
    fetchPolicy: 'no-cache',
  });
  return { ...res, data: res.data?.updateProgramAnswer ?? null };
};

export const deleteProgramAnswer = async (
  client: ApolloClient<object>,
  id: number,
): Promise<FetchResult<DeleteProgramAnswerPayload | null>> => {
  const res = await client.mutate<{
    deleteProgramAnswer: DeleteProgramAnswerPayload;
  }>({
    mutation: deleteProgramAnswerMutation,
    variables: { id },
    fetchPolicy: 'no-cache',
  });
  return { ...res, data: res.data?.deleteProgramAnswer ?? null };
};

export const updateProgramFields = async (
  client: ApolloClient<object>,
  id: number,
  fields: { name?: string; isCompleted?: boolean },
): Promise<FetchResult<UpdateProgramPayload | null>> => {
  const res = await client.mutate<{ updateProgram: UpdateProgramPayload }>({
    mutation: updateProgramMutation,
    variables: { id, ...fields },
    fetchPolicy: 'no-cache',
  });
  return { ...res, data: res.data?.updateProgram ?? null };
};

export const copyProgramAnswersToReport = async (
  client: ApolloClient<object>,
  reportId: number,
): Promise<FetchResult<CopyProgramAnswersPayload | null>> => {
  const res = await client.mutate<{
    copyProgramAnswersToReport: CopyProgramAnswersPayload;
  }>({
    mutation: copyProgramAnswersToReportMutation,
    variables: { reportId },
    fetchPolicy: 'no-cache',
  });
  return { ...res, data: res.data?.copyProgramAnswersToReport ?? null };
};
